/**
 * Mythium Alpha Cleanup
 *
 * Post-processing op @imgly's bg-removal output: kill semi-opaque "vuile
 * resten" rond de figuur door alpha-thresholding + nearest-decided-pixel
 * BFS voor de middenzone, plus optionele 1-2 px morphological erosion
 * van de buitenrand om soft halos weg te halen.
 *
 * Operates op de bg-removed File en geeft een nieuwe File terug. Past in
 * de pipeline NA bg-removal en VOOR hole-fill.
 */

(function () {
  'use strict';

  // Dialable thresholds. Alles < LOW wordt clear (alpha 0), alles > HIGH
  // wordt opaque (alpha 255). De mid-zone wordt gerepareerd via BFS naar
  // de dichtstbijzijnde beslissing.
  const CLEAN_ALPHA_LOW = 32;
  const CLEAN_ALPHA_HIGH = 224;
  // Hoeveel pixels van de opaque buitenrand wegnemen na binarisatie.
  // 0 = geen erosion. 1 = subtiel debluur. 2 = meer agressief.
  const EROSION_PX = 1;

  function loadImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
      img.src = url;
    });
  }

  function canvasToFile(canvas, baseName) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error('toBlob failed'));
        const name = baseName.replace(/\.[^.]+$/, '') + '-clean.png';
        resolve(new File([blob], name, { type: 'image/png' }));
      }, 'image/png');
    });
  }

  /**
   * Returns a new File with the alpha channel cleaned: binarised via
   * threshold + nearest-decided BFS for the mid-zone, optionally eroded.
   * Returns the original file unchanged if input isn't a PNG.
   */
  async function cleanAlpha(file) {
    if (!file || !/^image\/png/i.test(file.type)) return file;

    let img;
    try { img = await loadImage(file); } catch (e) { return file; }
    const W = img.naturalWidth, H = img.naturalHeight;
    if (!W || !H) return file;

    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0);

    let imgData;
    try { imgData = ctx.getImageData(0, 0, W, H); } catch (e) { return file; }
    const data = imgData.data;
    const N = W * H;

    // ─── Pass 1: threshold → decided (1=opaque, 2=clear) or undecided (0) ───
    // 1 = decided opaque (alpha → 255), 2 = decided clear (alpha → 0).
    const decision = new Uint8Array(N);
    let undecidedCount = 0;
    for (let i = 0; i < N; i++) {
      const a = data[i * 4 + 3];
      if (a >= CLEAN_ALPHA_HIGH) decision[i] = 1;
      else if (a <= CLEAN_ALPHA_LOW) decision[i] = 2;
      else undecidedCount++;
    }

    // ─── Pass 2: multi-source BFS from decided pixels ─────────────────
    if (undecidedCount > 0) {
      const queue = new Int32Array(N);
      let qHead = 0, qTail = 0;
      for (let i = 0; i < N; i++) {
        if (decision[i] !== 0) queue[qTail++] = i;
      }
      const yieldToMain = () => new Promise(r => setTimeout(r, 0));
      let bfsCounter = 0;
      while (qHead < qTail) {
        const idx = queue[qHead++];
        bfsCounter++;
        if ((bfsCounter & 0xFFFF) === 0) await yieldToMain();
        const v = decision[idx];
        const x = idx % W;
        const y = (idx - x) / W;
        const tryProp = (nIdx) => {
          if (decision[nIdx] === 0) {
            decision[nIdx] = v;
            queue[qTail++] = nIdx;
          }
        };
        if (x > 0)     tryProp(idx - 1);
        if (x < W - 1) tryProp(idx + 1);
        if (y > 0)     tryProp(idx - W);
        if (y < H - 1) tryProp(idx + W);
      }
    }

    // ─── Pass 3 (optional): morphological erosion of the opaque region ─
    // For each pass: any opaque pixel adjacent (4-conn) to a clear pixel
    // becomes clear. Erodes the rim by 1 px per pass.
    if (EROSION_PX > 0) {
      let current = decision;
      for (let pass = 0; pass < EROSION_PX; pass++) {
        const next = new Uint8Array(N);
        for (let i = 0; i < N; i++) next[i] = current[i];
        for (let y = 0; y < H; y++) {
          for (let x = 0; x < W; x++) {
            const idx = y * W + x;
            if (current[idx] !== 1) continue; // only erode opaque
            const left  = x > 0       ? current[idx - 1] : 2;
            const right = x < W - 1   ? current[idx + 1] : 2;
            const up    = y > 0       ? current[idx - W] : 2;
            const down  = y < H - 1   ? current[idx + W] : 2;
            if (left === 2 || right === 2 || up === 2 || down === 2) {
              next[idx] = 2; // erode to clear
            }
          }
        }
        current = next;
      }
      // Write back
      for (let i = 0; i < N; i++) decision[i] = current[i];
    }

    // ─── Apply final alpha. Keep original RGB; alpha is binarised. ─────
    for (let i = 0; i < N; i++) {
      data[i * 4 + 3] = (decision[i] === 1) ? 255 : 0;
    }
    ctx.putImageData(imgData, 0, 0);

    try {
      return await canvasToFile(canvas, file.name || 'artwork.png');
    } catch (e) {
      return file;
    }
  }

  window.MythiumAlphaCleanup = {
    cleanAlpha: cleanAlpha,
  };
})();
